import { ref, computed } from 'vue'
import { useStorage } from '@vueuse/core'
import { showToast } from 'vant'
import { t } from '@/i18n'
import { useUserStore } from '@/stores/user'
import type { IRes, IResPage, IReqPage } from '@/types/apis/base'

// ========== Layer 1: 通用 Map 序列化器（全项目唯一一份） ==========
export const createMapSerializer = <T>() => ({
  read: (v: string): Map<number, T> => {
    if (!v) return new Map()
    return new Map(JSON.parse(v) as [number, T][])
  },
  write: (v: Map<number, T>): string => JSON.stringify([...v.entries()]),
})

// ========== Layer 2: 通用分页 Store 工厂 ==========
export interface PaginatedStoreConfig<TItem> {
  /** sessionStorage 存储 key */
  storageKey: string
  /** 分页加载 API 函数 */
  fetchApi: (params: IReqPage) => Promise<IRes<IResPage<TItem>>>
  /** 从 API 返回的列表项中提取 Map key */
  getItemId: (item: TItem) => number
}

/**
 * 创建通用分页数据管理状态。
 * 管理 loading / loaded / hasMore / page 分页状态与 Map 数据的加载、清除。
 * 数据模型由泛型 TItem 决定，不做额外包装，保持扁平。
 */
export function usePaginatedStore<TItem>(config: PaginatedStoreConfig<TItem>) {
  const userStore = useUserStore()

  const dataMap = useStorage<Map<number, TItem>>(
    config.storageKey,
    new Map(),
    sessionStorage,
    { serializer: createMapSerializer<TItem>() },
  )

  const loading = ref(false)
  const loaded = ref(false)
  const hasMore = ref(true)
  const page = ref(1)

  /** 原始列表（不含排序等业务逻辑，由调用方自行 computed 覆盖） */
  const list = computed(() => [...dataMap.value.values()])

  /** 分页加载列表数据 */
  const loadList = async (pageNum?: number) => {
    if (!userStore.isLogin) return

    if (pageNum !== undefined) {
      page.value = pageNum
      if (pageNum === 1) {
        dataMap.value.clear()
        hasMore.value = true
      }
    }

    if (loading.value || !hasMore.value) return

    try {
      loading.value = true
      const res = await config.fetchApi({ PageIndex: page.value, PageSize: 20 })

      if (res.IsSuccess && res.Body) {
        const { Items, HasNextPage } = res.Body
        Items.forEach(item => {
          dataMap.value.set(config.getItemId(item), item)
        })
        hasMore.value = HasNextPage
        page.value += 1
        loaded.value = true
      }
    } catch {
      showToast(t('common.errors.loadFailed'))
    } finally {
      loading.value = false
    }
  }

  /** 清除所有数据与分页状态 */
  const clear = () => {
    dataMap.value.clear()
    loaded.value = false
    page.value = 1
    hasMore.value = true
  }

  return { dataMap, list, loading, loaded, hasMore, loadList, clear }
}
